import { createFileRoute, Link } from "@tanstack/react-router";
import { useEffect, useMemo, useRef, useState } from "react";
import {
  Image as ImageIcon,
  FileCode2,
  AlertCircle,
  Download,
  Copy,
  Check,
  Link2,
  Printer,
} from "lucide-react";
import { loadQRCode } from "@/lib/qrcode-lazy";
import { loadBwip } from "@/lib/bwip";
import { ToolLayout } from "@/components/layout/ToolLayout";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Card, CardContent } from "@/components/ui/card";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { FaqSection } from "@/components/seo/FaqSection";
import { ToolSeoSections } from "@/components/seo/ToolSeoSections";
import { pageHead, breadcrumbJsonLd, softwareAppJsonLd, ldScript } from "@/lib/seo";
import { PrintSetupDialog } from "@/components/print/PrintSetupDialog";
import { computeCheckDigit } from "@/lib/gtin";

const PATH = "/gs1-digital-link-generator";
const TITLE = "GS1 Digital Link Generator — Build Resolver URLs as QR / DM";
const DESC =
  "Free GS1 Digital Link generator. Build a Sunrise-2027 resolver URL from a GTIN plus optional AIs and render it as a QR or Data Matrix barcode.";

const FAQ = [
  {
    q: "What is a GS1 Digital Link?",
    a: "A GS1 Digital Link is a web URL that carries GTINs and other GS1 Application Identifiers in a structured, scannable format. Example: https://id.gs1.org/01/09506000134352/10/ABC123 encodes GTIN 09506000134352 with batch ABC123. Brands use it to link a single 2D code to product info, traceability data, instructions and marketing.",
  },
  {
    q: "What Is Sunrise 2027?",
    a: "Sunrise 2027 is GS1's global initiative for retailers and brands to be ready to accept 2D barcodes (QR / Data Matrix encoding GS1 Digital Link) at point of sale by the end of 2027. This generator produces URLs that conform to the GS1 Digital Link URI Syntax used by Sunrise 2027.",
  },
  {
    q: "Which AIs Are Supported Here?",
    a: "GTIN (01) plus the four most common qualifiers and attributes: batch/lot (10), serial (21), expiry YYMMDD (17), best-before YYMMDD (15) and net weight in kg with three decimals (3103). Path AIs (10, 21) are placed in the URL path; data attributes (15, 17, 3103) go in the query string per the GS1 DL standard.",
  },
  {
    q: "QR or Data Matrix — Which Should I Pick?",
    a: "Both are valid for GS1 Digital Link. QR is friendlier for consumer phones; Data Matrix is the established choice in healthcare, fresh food (variable-weight) and small-item marking. If you already use Data Matrix on packaging, stay with it.",
  },
  {
    q: "Will Retailer Scanners Actually Accept It?",
    a: "Modern POS scanners increasingly recognise GS1 Digital Link URLs and extract the GTIN automatically — that is the whole point of Sunrise 2027. Always test with the actual scanner model your retailer uses before mass-printing.",
  },
  {
    q: "Is the URL I Enter Sent Anywhere?",
    a: "No. The URL is composed and rendered locally with qrcode and bwip-js — no server roundtrip, no logging.",
  },
  {
    q: "Which Barcodes Can Carry a GS1 Digital Link?",
    a: "It needs a 2D barcode that can hold a full URL, and QR Code and Data Matrix are the two this generator produces. EAN-13 and UPC-A hold only digits, so they cannot carry the link. GS1-128 encodes GS1 element strings rather than URLs, so it is not used for Digital Link either.",
  },
];

const CRUMBS = [
  { name: "Home", path: "/" },
  { name: "Generators", path: "/generators" },
  { name: "GS1 Digital Link Generator", path: PATH },
];

type RenderAs = "qr" | "dm" | "url";

function normalizeGtin(input: string): string | null {
  const raw = input.replace(/\s+/g, "");
  if (!/^\d+$/.test(raw)) return null;
  if (![8, 12, 13, 14].includes(raw.length)) return null;
  return raw.padStart(14, "0");
}

function validateGtin(input: string): string | null {
  const raw = input.replace(/\s+/g, "");
  if (raw.length === 0) return "Enter a GTIN-8, 12, 13 or 14.";
  if (!/^\d+$/.test(raw)) return "GTIN must contain digits only.";
  if (![8, 12, 13, 14].includes(raw.length))
    return `GTIN must be 8, 12, 13 or 14 digits — you entered ${raw.length}.`;
  const body = raw.slice(0, -1);
  const given = Number(raw[raw.length - 1]);
  const expected = computeCheckDigit(body);
  if (given !== expected) return `GTIN check digit mismatch — expected ${expected}, got ${given}.`;
  return null;
}

function isAlnumGs1(v: string): boolean {
  // GS1 AI 10/21 character set: most printable ASCII; we accept A-Z a-z 0-9 - . / and underscore
  return /^[A-Za-z0-9\-./_]+$/.test(v);
}

function isYymmdd(v: string): boolean {
  if (!/^\d{6}$/.test(v)) return false;
  const mm = Number(v.slice(2, 4));
  const dd = Number(v.slice(4, 6));
  // DD may be 00 per GS1 to mean "no specific day"
  return mm >= 1 && mm <= 12 && dd >= 0 && dd <= 31;
}

type Inputs = {
  resolver: string;
  gtin: string;
  batch: string;
  serial: string;
  expiry: string;
  bestBefore: string;
  netWeight: string;
};

function validateAll(input: Inputs): string | null {
  try {
    const u = new URL(input.resolver);
    if (u.protocol !== "https:" && u.protocol !== "http:")
      return "Resolver must be an http(s) URL.";
  } catch {
    return "Resolver must be a valid URL (e.g. https://id.gs1.org).";
  }

  const gtinErr = validateGtin(input.gtin);
  if (gtinErr) return gtinErr;

  if (input.batch) {
    if (input.batch.length > 20) return "Batch/lot (AI 10) must be 20 characters or fewer.";
    if (!isAlnumGs1(input.batch)) return "Batch/lot (AI 10) contains unsupported characters.";
  }
  if (input.serial) {
    if (input.serial.length > 20) return "Serial (AI 21) must be 20 characters or fewer.";
    if (!isAlnumGs1(input.serial)) return "Serial (AI 21) contains unsupported characters.";
  }
  if (input.expiry && !isYymmdd(input.expiry))
    return "Expiry (AI 17) must be 6 digits in YYMMDD format.";
  if (input.bestBefore && !isYymmdd(input.bestBefore))
    return "Best-before (AI 15) must be 6 digits in YYMMDD format.";
  if (input.netWeight && !/^\d{6}$/.test(input.netWeight))
    return "Net weight (AI 3103) must be exactly 6 digits.";

  return null;
}

function buildUrl(input: Inputs): string {
  const gtin14 = normalizeGtin(input.gtin)!;
  const base = input.resolver.replace(/\/+$/, "");
  let path = `/01/${gtin14}`;
  if (input.batch) path += `/10/${encodeURIComponent(input.batch)}`;
  if (input.serial) path += `/21/${encodeURIComponent(input.serial)}`;
  const params: string[] = [];
  if (input.expiry) params.push(`17=${input.expiry}`);
  if (input.bestBefore) params.push(`15=${input.bestBefore}`);
  if (input.netWeight) params.push(`3103=${input.netWeight}`);
  return base + path + (params.length ? `?${params.join("&")}` : "");
}

function safeFilename(gtin: string): string {
  return `gs1-dl-${gtin.replace(/\D/g, "").slice(0, 14) || "code"}`;
}

function GS1DigitalLinkGeneratorPage() {
  const [resolver, setResolver] = useState("https://id.gs1.org");
  const [gtin, setGtin] = useState("09506000134352");
  const [batch, setBatch] = useState("");
  const [serial, setSerial] = useState("");
  const [expiry, setExpiry] = useState("");
  const [bestBefore, setBestBefore] = useState("");
  const [netWeight, setNetWeight] = useState("");
  const [renderAs, setRenderAs] = useState<RenderAs>("qr");

  const [error, setError] = useState<string | null>(null);
  const [svgMarkup, setSvgMarkup] = useState("");
  const [copied, setCopied] = useState(false);
  const [printOpen, setPrintOpen] = useState(false);

  const canvasRef = useRef<HTMLCanvasElement | null>(null);

  const inputs: Inputs = useMemo(
    () => ({ resolver, gtin, batch, serial, expiry, bestBefore, netWeight }),
    [resolver, gtin, batch, serial, expiry, bestBefore, netWeight],
  );

  const validationErr = validateAll(inputs);
  const url = validationErr ? "" : buildUrl(inputs);

  useEffect(() => {
    if (validationErr) {
      setError(validationErr);
      setSvgMarkup("");
      const c = canvasRef.current;
      if (c) c.getContext("2d")?.clearRect(0, 0, c.width, c.height);
      return;
    }
    if (renderAs === "url") {
      setError(null);
      setSvgMarkup("");
      const c = canvasRef.current;
      if (c) c.getContext("2d")?.clearRect(0, 0, c.width, c.height);
      return;
    }
    let cancelled = false;
    (async () => {
      try {
        if (renderAs === "qr") {
          const QRCode = await loadQRCode();
          if (canvasRef.current) {
            await QRCode.toCanvas(canvasRef.current, url, {
              errorCorrectionLevel: "M",
              margin: 2,
              width: 320,
            });
          }
          const svg = await QRCode.toString(url, {
            type: "svg",
            errorCorrectionLevel: "M",
            margin: 2,
            width: 320,
          });
          if (cancelled) return;
          setSvgMarkup(svg);
        } else {
          const bwipjs = await loadBwip();
          const opts = {
            bcid: "datamatrix",
            text: url,
            scale: 4,
            backgroundcolor: "FFFFFF",
            padding: 8,
          };
          if (canvasRef.current) bwipjs.toCanvas(canvasRef.current, opts);
          const svg = bwipjs.toSVG(opts);
          if (cancelled) return;
          setSvgMarkup(svg);
        }
        setError(null);
      } catch (e) {
        if (cancelled) return;
        const msg = e instanceof Error ? e.message : "Failed to render code.";
        setError(msg);
        setSvgMarkup("");
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [url, renderAs, validationErr]);

  const downloadPng = () => {
    const c = canvasRef.current;
    if (!c) return;
    c.toBlob((blob) => {
      if (!blob) return;
      const u = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = u;
      a.download = `${safeFilename(gtin)}.png`;
      a.click();
      URL.revokeObjectURL(u);
    }, "image/png");
  };

  const downloadSvg = () => {
    if (!svgMarkup) return;
    const blob = new Blob([svgMarkup], { type: "image/svg+xml" });
    const u = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = u;
    a.download = `${safeFilename(gtin)}.svg`;
    a.click();
    URL.revokeObjectURL(u);
  };

  const copyUrl = async () => {
    if (!url) return;
    try {
      await navigator.clipboard.writeText(url);
      setCopied(true);
      setTimeout(() => setCopied(false), 1500);
    } catch {
      /* ignore */
    }
  };

  return (
    <ToolLayout
      eyebrow="Online Tool"
      title="GS1 Digital Link Generator"
      description="Build a Sunrise-2027-ready GS1 Digital Link URL from a GTIN and optional AIs, and render it as a QR code or Data Matrix. It verifies the GTIN check digit, pads GTIN-8, 12 and 13 to 14 digits, and exports the symbol as PNG or SVG or sends it to a print layout."
      crumbs={CRUMBS}
      content={
        <>
          <ToolSeoSections
            aboutHeading="About the GS1 Digital Link generator"
            about={
              <>
                <p>
                  The ReadBarcode GS1 Digital Link generator builds a standards-compliant resolver URL
                  from a GTIN and the most common GS1 Application Identifiers, then renders it as a QR
                  code or Data Matrix. The URL follows the GS1 Digital Link URI Syntax — the same format
                  retailers and brands are adopting for Sunrise 2027.
                </p>
                <p>
                  GTINs are normalised to 14 digits and check-digit verified, batch and serial are
                  placed in the URL path, and dated attributes (expiry, best-before, net weight) are
                  appended as query parameters. Everything runs in your browser with the open-source
                  qrcode and bwip-js libraries.
                </p>
              </>
            }
            whenToUse={[
              "Trialling Sunrise 2027 compatible labels on consumer packaging.",
              "Linking a single 2D code to product info, instructions and traceability.",
              "Encoding GTIN + batch + expiry for healthcare or food shipments.",
              "Generating variable-weight Data Matrix codes for fresh produce or deli.",
              "Migrating from GS1-128 to a Digital Link URL on the same artwork.",
              "Producing test codes for POS, e-commerce and warehouse scanner trials.",
            ]}
            howItWorks={[
              {
                title: "Pick a resolver",
                desc: "Default to https://id.gs1.org or point to your brand resolver if you operate one.",
              },
              {
                title: "Enter the GTIN",
                desc: "GTIN-8/12/13/14. The check digit is verified and the value is padded to 14 digits.",
              },
              {
                title: "Add AIs as needed",
                desc: "Batch (10), serial (21) and dated attributes (15, 17, 3103). All optional.",
              },
              {
                title: "Choose QR or DM",
                desc: "QR for consumer phones; Data Matrix for healthcare, fresh food and small parts.",
              },
              {
                title: "Download PNG or SVG",
                desc: "SVG for print artwork; PNG for screens and quick proofs.",
              },
            ]}
            whatToAvoid={[
              "Hand-rolling the URL — get the path-vs-query split for path AIs vs data attributes wrong and many scanners will reject it.",
              "Skipping the GTIN check digit — the URL will validate as a string but fail every conformance check.",
              "Encoding sensitive data (lot recall flags, internal SKUs) in the URL — anyone scanning sees it in plain text.",
              "Forgetting to print human-readable GTIN + batch + expiry near the 2D code for backup.",
              "Rolling out at scale before testing on the actual retailer's POS scanner.",
            ]}
            tips={[
              "Use this together with the GS1 Digital Link Decoder on this site to round-trip-test your output.",
              "Keep URLs short — the longer the payload, the denser (and harder to scan) the 2D code.",
              "For variable-weight items, pair GTIN + 3103 (net weight) — most POS systems already understand it.",
              "Stick to https:// resolvers — some scanners reject http for security policy reasons.",
              "If you migrate from GS1-128, keep the same GTIN so existing master data still matches.",
            ]}
          />
          <PrivacyNote />
          <DigitalLinkSections />
          <FaqSection items={FAQ} />
        </>
      }
      related={<RelatedTools />}
    >
      <div className="mt-6 grid gap-6 md:grid-cols-2">
        <Card>
          <CardContent className="space-y-5 p-6">
            <div className="space-y-2">
              <Label htmlFor="resolver">Resolver domain</Label>
              <Input
                id="resolver"
                value={resolver}
                onChange={(e) => setResolver(e.target.value)}
                placeholder="https://id.gs1.org"
                spellCheck={false}
                autoComplete="off"
              />
              <p className="text-xs text-muted-foreground">
                Default <code>https://id.gs1.org</code>. Replace with your own brand resolver if you
                have one.
              </p>
            </div>

            <div className="space-y-2">
              <Label htmlFor="gtin">GTIN (01) — required</Label>
              <Input
                id="gtin"
                value={gtin}
                onChange={(e) => setGtin(e.target.value.replace(/\s+/g, ""))}
                placeholder="09506000134352"
                inputMode="numeric"
                maxLength={14}
                spellCheck={false}
                autoComplete="off"
                className="font-mono"
              />
              <p className="text-xs text-muted-foreground">
                GTIN-8, 12, 13 or 14. Check digit is verified; shorter values are padded to GTIN-14.
              </p>
            </div>

            <div className="grid grid-cols-2 gap-3">
              <div className="space-y-2">
                <Label htmlFor="batch">Batch / lot (10)</Label>
                <Input
                  id="batch"
                  value={batch}
                  onChange={(e) => setBatch(e.target.value)}
                  placeholder="ABC123"
                  maxLength={20}
                  spellCheck={false}
                  autoComplete="off"
                  className="font-mono"
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="serial">Serial (21)</Label>
                <Input
                  id="serial"
                  value={serial}
                  onChange={(e) => setSerial(e.target.value)}
                  placeholder="SN-0001"
                  maxLength={20}
                  spellCheck={false}
                  autoComplete="off"
                  className="font-mono"
                />
              </div>
            </div>

            <div className="grid grid-cols-2 gap-3">
              <div className="space-y-2">
                <Label htmlFor="expiry">Expiry — YYMMDD (17)</Label>
                <Input
                  id="expiry"
                  value={expiry}
                  onChange={(e) => setExpiry(e.target.value.replace(/\D/g, ""))}
                  placeholder="261231"
                  inputMode="numeric"
                  maxLength={6}
                  className="font-mono"
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="bestBefore">Best-before — YYMMDD (15)</Label>
                <Input
                  id="bestBefore"
                  value={bestBefore}
                  onChange={(e) => setBestBefore(e.target.value.replace(/\D/g, ""))}
                  placeholder="261001"
                  inputMode="numeric"
                  maxLength={6}
                  className="font-mono"
                />
              </div>
            </div>

            <div className="space-y-2">
              <Label htmlFor="netWeight">Net weight kg, 3 decimals (3103)</Label>
              <Input
                id="netWeight"
                value={netWeight}
                onChange={(e) => setNetWeight(e.target.value.replace(/\D/g, ""))}
                placeholder="001250 = 1.250 kg"
                inputMode="numeric"
                maxLength={6}
                className="font-mono"
              />
              <p className="text-xs text-muted-foreground">
                6 digits, implied 3 decimal places. <code>001250</code> means 1.250 kg.
              </p>
            </div>

            <div className="space-y-2">
              <Label htmlFor="renderAs">Render as</Label>
              <Select value={renderAs} onValueChange={(v) => setRenderAs(v as RenderAs)}>
                <SelectTrigger id="renderAs">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="qr">QR Code</SelectItem>
                  <SelectItem value="dm">Data Matrix</SelectItem>
                  <SelectItem value="url">URL only</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardContent className="flex h-full flex-col gap-4 p-6">
            <div className="flex min-h-[280px] flex-1 items-center justify-center rounded-md border border-dashed border-border bg-white p-4">
              {error ? (
                <div className="flex items-center gap-2 text-sm text-destructive">
                  <AlertCircle className="h-4 w-4" aria-hidden />
                  <span>{error}</span>
                </div>
              ) : renderAs === "url" ? (
                <div className="flex flex-col items-center gap-2 text-center">
                  <Link2 className="h-8 w-8 text-primary" aria-hidden />
                  <p className="text-xs text-muted-foreground">
                    URL-only mode — copy the link below.
                  </p>
                </div>
              ) : (
                <canvas
                  ref={canvasRef}
                  className="max-h-[420px] max-w-full"
                  aria-label="Generated GS1 Digital Link preview"
                />
              )}
            </div>

            <div className="rounded-md border border-border bg-muted/30 p-3">
              <div className="flex items-start justify-between gap-2">
                <div className="min-w-0">
                  <div className="text-xs font-medium text-muted-foreground">
                    GS1 Digital Link URL
                  </div>
                  <code className="mt-1 block break-all text-xs">{url || "—"}</code>
                </div>
                <Button type="button" variant="ghost" size="sm" onClick={copyUrl} disabled={!url}>
                  {copied ? <Check className="h-3.5 w-3.5" /> : <Copy className="h-3.5 w-3.5" />}
                  {copied ? "Copied" : "Copy"}
                </Button>
              </div>
            </div>

            {renderAs !== "url" && (
              <div className="grid grid-cols-3 gap-2">
                <Button onClick={downloadPng} disabled={!!error}>
                  <ImageIcon className="h-4 w-4" aria-hidden />
                  PNG
                </Button>
                <Button variant="outline" onClick={downloadSvg} disabled={!!error || !svgMarkup}>
                  <FileCode2 className="h-4 w-4" aria-hidden />
                  SVG
                </Button>
                <Button
                  variant="outline"
                  onClick={() => setPrintOpen(true)}
                  disabled={!!error || !svgMarkup}
                >
                  <Printer className="h-4 w-4" aria-hidden />
                  Print
                </Button>
              </div>
            )}
            <p className="flex items-center gap-1.5 text-xs text-muted-foreground">
              <Download className="h-3.5 w-3.5" aria-hidden />
              Files are generated on your device — nothing is uploaded.
            </p>
          </CardContent>
        </Card>
      </div>

      <PrintSetupDialog
        open={printOpen}
        onOpenChange={setPrintOpen}
        title={`GS1 Digital Link · ${gtin || "code"}`}
        filename={safeFilename(gtin)}
        svgMarkup={svgMarkup}
        defaultCaption={url || gtin}
        defaults={{
          layoutKind: "single",
          widthMm: 40,
          sheetPreset: "averyL7160",
          paper: "A4",
          orientation: "portrait",
        }}
      />
    </ToolLayout>
  );
}

function PrivacyNote() {
  return (
    <>
      <h2>Private by Default</h2>
      <p>
        URLs are composed and rendered locally with qrcode and bwip-js. The GTIN, batch, serial
        and other values you enter never touch our servers.
      </p>
    </>
  );
}

function DigitalLinkSections() {
  return (
    <>
      <h2>GS1 Digital Link Example You Can Copy</h2>
      <p>The defaults produce a working example for GTIN 09506000134352, the sample GTIN in GS1's documentation, and optional fields extend it:</p>
      <figure>
        <pre>
          <code>{`https://id.gs1.org/01/09506000134352
https://id.gs1.org/01/09506000134352/10/ABC123?17=261231`}</code>
        </pre>
      </figure>
      <p>Batch and serial go in the path, while expiry, best-before and net weight go in the query string, so 17=261231 is an expiry of 31 December 2026. Paste the result into the <Link to="/gs1-digital-link-decoder">GS1 Digital Link decoder</Link> to check each value, or see the <Link to="/barcode-guides/gs1-digital-link">GS1 Digital Link guide</Link> for the reasoning behind the split.</p>
      <h2>GS1 Digital Link QR Code or Data Matrix</h2>
      <p>Both symbols carry the same URL, so pick the one your scanners and artwork already support.</p>
      <p>As a GS1 Digital Link QR code generator, the page uses error correction level M and a 2-module margin, so leave more white space on the artwork to reach the 4-module quiet zone the QR standard asks for. The <Link to="/barcode-guides/sunrise-2027-explained">Sunrise 2027 guide</Link> explains why checkout scanners are moving to 2D.</p>
      <h3>GS1 Digital Link Data Matrix Generator</h3>
      <p>In Data Matrix mode, bwip-js picks the smallest square symbol that holds the URL, and each extra Application Identifier enlarges the grid. Choose URL only when your label software draws the symbol and needs just the link text from this GS1 Digital Link creator.</p>
    </>
  );
}

function RelatedTools() {
  const related = [
    {
      path: "/barcode-guides/gs1-digital-link",
      label: "GS1 Digital Link Guide",
      desc: "How the URI syntax, resolvers and AIs fit together.",
    },
    {
      path: "/gs1-digital-link-decoder",
      label: "GS1 Digital Link Decoder",
      desc: "Round-trip-test the URL you just generated.",
    },
    {
      path: "/barcode-guides/sunrise-2027-explained",
      label: "Sunrise 2027 Guide",
      desc: "Why retailers are switching to 2D barcodes by 2027.",
    },
    {
      path: "/gtin-validator",
      label: "GTIN Validator",
      desc: "Verify a GTIN-8/12/13/14 check digit.",
    },
    {
      path: "/qr-code-generator",
      label: "QR Code Generator",
      desc: "Plain QR codes with full styling control.",
    },
  ];
  return (
    <section className="mt-12">
      <h2 className="text-lg font-semibold">Related Tools</h2>
      <div className="mt-4 grid gap-3 sm:grid-cols-2">
        {related.map((r) => (
          <a
            key={r.path}
            href={r.path}
            className="group rounded-lg border border-border bg-background p-4 transition-colors hover:border-primary/40"
          >
            <div className="text-sm font-medium group-hover:text-primary">{r.label}</div>
            <div className="text-xs text-muted-foreground">{r.desc}</div>
          </a>
        ))}
      </div>
    </section>
  );
}

export const Route = createFileRoute("/gs1-digital-link-generator")({
  component: GS1DigitalLinkGeneratorPage,
  head: () => {
    const base = pageHead({
      title: TITLE,
      description: DESC,
      path: PATH,
    });
    return {
      ...base,
      scripts: [
        ldScript(
          softwareAppJsonLd({
            name: "ReadBarcode GS1 Digital Link Generator",
            description: DESC,
            path: PATH,
          }),
        ),
        ldScript(breadcrumbJsonLd(CRUMBS)),
      ],
    };
  },
});
